import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { Upload } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog, CsvTemplateHint, CreatableSelect, InfoTip, StatusChip } from '../../ui';
import { taxonomyTemplateProps } from '../taxonomyTemplate';
import { UnmatchedProductsSection } from '../../ShopifyCategoryMappingSection';
import {
  integrationApiService,
  MerchantTaxonomyEntry,
  MerchantTaxonomyImportPreview,
  MerchantTaxonomyRegistry,
  MerchantTaxonomyStats,
  ShopifyTaxonomyOption,
} from '../../../services/integrationApi';
import { useToast } from '../../../contexts/ToastContext';
import { useAppTranslation } from '../../../i18n/useAppTranslation';
import { tokens } from '../../../theme/tokens';
import { countPrefixMatches, MappingIndexRow, suggestSkuPrefixes } from '../../../utils/mappingIndex';
import { taxonomyLabel, taxonomyShortLabel } from '../mappingModel';
import ShopifyCategoryPicker from '../ShopifyCategoryPicker';
import { LinkAction, StepCircle, v2CardSx, v2Mono, V2_TAXONOMY_QUERY_KEY } from './v2Ui';

export type TaxonomyDraft = Partial<Pick<MerchantTaxonomyEntry, 'skuPrefix' | 'shopifyCategoryGid' | 'productType'>>;
/** Unsaved edits to registry entries, by entry id. The page-level Save writes them. */
export type TaxonomyDrafts = Record<string, TaxonomyDraft>;

const PREVIEW_ROWS = 3;
// The preview table scrolls horizontally (minWidth below), so it keeps its columns at every width.
const PREVIEW_COLUMNS = { xs: '90px minmax(0,1fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1.2fr)' };
const EDIT_COLUMNS = { xs: 'minmax(0,1fr)', md: 'minmax(0,1.1fr) 150px minmax(0,1fr) minmax(0,1fr)' };

interface TaxonomyStepProps {
  n: number;
  registry: MerchantTaxonomyRegistry | null;
  stats: MerchantTaxonomyStats | undefined;
  loading: boolean;
  /** Curated categories plus any stored or drafted GID outside them, labelled by breadcrumb (`useShopifyTaxonomyOptions`). */
  taxonomyOptions: ShopifyTaxonomyOption[];
  typeOptions: string[];
  integrationId: string;
  drafts: TaxonomyDrafts;
  onDraftsChange: (drafts: TaxonomyDrafts) => void;
  /** Flag `structured_mapping_inputs`. */
  indexRows?: MappingIndexRow[];
}

const headerSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: tokens.ink.muted,
} as const;

/** Preview rows are a fixed height, so a long value truncates (full text in `title`) instead of wrapping into the next row. */
const cellSx = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

/**
 * Step 1: the store's own department / category / subcategory list. A store SKU prefix picks the entry,
 * and the entry sets both product type and Shopify category. An upload replaces the list straight away
 * (it belongs to the store, not this integration); edits to entries wait for the page's Save.
 */
const TaxonomyStep: React.FC<TaxonomyStepProps> = ({
  n,
  registry,
  stats,
  loading,
  taxonomyOptions,
  typeOptions,
  integrationId,
  drafts,
  onDraftsChange,
  indexRows,
}) => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useAppTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [filter, setFilter] = useState('');
  const [pendingImport, setPendingImport] = useState<{ file: File; preview: MerchantTaxonomyImportPreview } | null>(null);

  const leaves = useMemo(() => (registry?.entries || []).filter((entry) => entry.level === 'subcategory'), [registry]);
  const effective = (entry: MerchantTaxonomyEntry) => ({
    prefix: drafts[entry.id]?.skuPrefix ?? entry.skuPrefix ?? '',
    gid: drafts[entry.id]?.shopifyCategoryGid ?? entry.shopifyCategoryGid ?? '',
    type: drafts[entry.id]?.productType ?? entry.productType ?? '',
  });
  const edit = (id: string, change: TaxonomyDraft) => onDraftsChange({ ...drafts, [id]: { ...drafts[id], ...change } });

  const prefixOptions = useMemo(() => {
    if (!indexRows) return [];
    const claimed = leaves.map((entry) => drafts[entry.id]?.skuPrefix ?? entry.skuPrefix ?? '');
    return suggestSkuPrefixes(indexRows, claimed).map(({ prefix, count }) => ({ value: prefix, hint: `${count} SKU${count === 1 ? '' : 's'} in your selection` }));
  }, [indexRows, leaves, drafts]);

  const resetInput = () => {
    if (fileInput.current) fileInput.current.value = '';
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const result = await integrationApiService.importMerchantTaxonomy(file);
      queryClient.setQueryData(V2_TAXONOMY_QUERY_KEY, { registry: result.registry, stats: result.stats });
      onDraftsChange({});
      const warnings = result.warnings || [];
      const stripped = warnings.length > 0
        ? ` ${t('csvTemplate.taxonomy.prefixStripped', { count: warnings.length, row: warnings[0].row, message: warnings[0].message })}`
        : '';
      showToast(
        (result.errors.length > 0
          ? `Imported ${result.stats.leaves} subcategories; ${result.errors.length} row${result.errors.length === 1 ? '' : 's'} skipped (row ${result.errors[0].row}: ${result.errors[0].message})`
          : `Imported ${result.stats.leaves} subcategories`) + stripped,
        result.errors.length > 0 || warnings.length > 0 ? 'warning' : 'success',
      );
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to import the taxonomy', 'error');
    } finally {
      setBusy(false);
      resetInput();
    }
  };

  /** Checks the file first. A file that removes subcategories asks before it replaces the list. */
  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    try {
      const preview = await integrationApiService.previewMerchantTaxonomyImport(file);
      if (preview.wouldRemove.leaves > 0) {
        setPendingImport({ file, preview });
        return;
      }
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to check the taxonomy file', 'error');
      resetInput();
      return;
    } finally {
      setBusy(false);
    }
    await upload(file);
  };

  const needle = filter.trim().toLowerCase();
  const editable = leaves.filter((entry) =>
    !needle || [entry.department, entry.category, entry.subcategory, entry.subcategoryCode, entry.skuPrefix]
      .some((value) => String(value || '').toLowerCase().includes(needle)),
  );
  const entryCount = stats?.leaves ?? leaves.length;
  const shown = leaves.slice(0, PREVIEW_ROWS);

  return (
    <Box sx={{ ...v2CardSx, px: 2.25, py: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <ConfirmDialog
        open={Boolean(pendingImport)}
        title="Replace your Store Taxonomy?"
        message={
          pendingImport
            ? `This file removes ${pendingImport.preview.wouldRemove.leaves} subcategories (${pendingImport.preview.wouldRemove.mapped} of them mapped). Continue?`
            : ''
        }
        preview={
          pendingImport && pendingImport.preview.wouldRemove.ids.length > 0 ? (
            <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: 1.5 }}>
              Removed: {pendingImport.preview.wouldRemove.ids.join(', ')}
              {pendingImport.preview.wouldRemove.leaves > pendingImport.preview.wouldRemove.ids.length ? ', …' : ''}
              {(pendingImport.preview.wouldDropAssignments ?? 0) > 0
                ? ` ${pendingImport.preview.wouldDropAssignments} product assignment${pendingImport.preview.wouldDropAssignments === 1 ? '' : 's'} on them will be dropped.`
                : ''}
            </Typography>
          ) : undefined
        }
        confirmLabel="Replace"
        confirmColor="warning"
        loading={busy}
        onClose={() => {
          setPendingImport(null);
          resetInput();
        }}
        onConfirm={() => {
          const file = pendingImport?.file;
          setPendingImport(null);
          if (file) void upload(file);
        }}
      />
      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv"
        hidden
        data-testid="taxonomy-file"
        onChange={(event) => pickFile(event.target.files?.[0])}
      />

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75, flexWrap: 'wrap' }}>
        <StepCircle n={n} tone="info" />
        <Box sx={{ flex: '1 1 360px', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Store Taxonomy</Typography>
            <InfoTip
              label="About Store Taxonomy"
              title="Your Store Taxonomy is your own list of departments, categories and subcategories, each with a SKU prefix. It outranks every step below, and it is optional: skip it if you do not keep one."
            />
          </Box>
          <Typography sx={{ fontSize: 12.5, color: tokens.ink.muted, mt: '3px' }}>
            Matches each product by its store SKU, e.g. CAM-121-00001 → entry CAM-121. A matching entry&apos;s Shopify Product Type and Shopify Product Category win over every step below.
          </Typography>
        </Box>
        {!registry && !loading && (
          <Box sx={{ flex: '0 0 auto', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <CsvTemplateHint compact size="medium" {...taxonomyTemplateProps} intro={t('csvTemplate.taxonomy.intro')} />
            <Button
              variant="outlined"
              startIcon={<Upload sx={{ fontSize: 16 }} />}
              disabled={busy}
              onClick={() => fileInput.current?.click()}
              sx={{ height: 34, borderColor: tokens.line.default, borderRadius: '9px', color: tokens.ink.body, fontWeight: 600 }}
            >
              Upload CSV
            </Button>
          </Box>
        )}
      </Box>

      {loading && <Box sx={{ ml: 4.5 }}><CircularProgress size={18} /></Box>}

      {!loading && !registry && (
        <Box sx={{ ml: { sm: 4.5 }, px: 1.75, py: 1.5, border: '1px dashed', borderColor: tokens.ink.disabled, borderRadius: '10px', fontSize: 12.5, color: tokens.ink.muted }}>
          Not using one. Columns needed:{' '}
          <Box component="span" sx={{ ...v2Mono, color: tokens.ink.body }}>Department, Code, Category, Subcategory, Subcategory Code</Box>
          . A new upload replaces the list.
          <Box sx={{ mt: 0.75 }}>{t('csvTemplate.taxonomy.codePrefix')}</Box>
          <Box sx={{ mt: 0.75 }}>{t('csvTemplate.taxonomy.optionalColumns')}</Box>
          <Box sx={{ mt: 1 }}>
            <CsvTemplateHint explainerOnly {...taxonomyTemplateProps} intro={t('csvTemplate.taxonomy.intro')} />
          </Box>
        </Box>
      )}

      {registry && (
        <Box sx={{ ml: { sm: 4.5 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', overflow: 'hidden' }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: 680 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: PREVIEW_COLUMNS, columnGap: 1.5, px: 1.75, height: 32, alignItems: 'center', bgcolor: tokens.surface.sunken }}>
                  {['Code', 'Category', 'Subcategory', 'Shopify Product Type', 'Shopify Product Category'].map((label) => (
                    <Typography key={label} sx={{ ...headerSx, ...cellSx }} title={label}>{label}</Typography>
                  ))}
                </Box>
                {shown.map((entry) => {
                  const value = effective(entry);
                  const code = value.prefix || entry.subcategoryCode || '—';
                  const category = entry.category || entry.department;
                  const productCategory = value.gid ? taxonomyShortLabel(taxonomyLabel(taxonomyOptions, value.gid)) : '—';
                  return (
                    <Box
                      key={entry.id}
                      sx={{ display: 'grid', gridTemplateColumns: PREVIEW_COLUMNS, columnGap: 1.5, px: 1.75, height: 36, alignItems: 'center', borderTop: '1px solid', borderColor: tokens.line.hairline, fontSize: 12.5 }}
                    >
                      <Box component="span" sx={{ ...v2Mono, ...cellSx }} title={code}>{code}</Box>
                      <Box component="span" sx={cellSx} title={category}>{category}</Box>
                      <Box component="span" sx={{ ...cellSx, color: tokens.ink.muted }} title={entry.subcategory}>{entry.subcategory}</Box>
                      <Box component="span" sx={cellSx} title={value.type || undefined}>{value.type || '—'}</Box>
                      <Box component="span" sx={cellSx} title={productCategory}>{productCategory}</Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1.75, py: 1.25, borderTop: '1px solid', borderColor: tokens.line.hairline, bgcolor: tokens.surface.sunken, fontSize: 12.5, color: tokens.ink.muted }}>
              <Box component="span" sx={{ flex: '1 1 auto' }}>
                {registry.importedFileName ? `${registry.importedFileName} · ` : ''}
                {entryCount} entries · {shown.length} shown
              </Box>
              <LinkAction onClick={() => setEditing((value) => !value)}>{editing ? 'Hide entries' : 'Edit entries'}</LinkAction>
              <LinkAction onClick={() => fileInput.current?.click()} disabled={busy}>Replace</LinkAction>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>
            Replaces the Shopify Product Category already on the listing unless it is set to Fill blanks or Never touch.
          </Typography>
          <Typography sx={{ fontSize: 12, color: tokens.ink.muted }}>{t('csvTemplate.taxonomy.codePrefix')}</Typography>

          {editing && (
            // `clip`, not `hidden`: rounds the corners without becoming a scroll container, so the header sticks to the page.
            <Box data-testid="taxonomy-edit-frame" sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', overflow: 'clip' }}>
              <Box sx={{ px: 1.75, py: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                  {stats?.mapped ?? 0} of {stats?.leaves ?? leaves.length} subcategories mapped
                </Typography>
                <TextField
                  size="small"
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  placeholder="Filter by department, category or code"
                  inputProps={{ 'aria-label': 'Filter Store Taxonomy' }}
                  sx={{ flex: '1 1 220px', '& .MuiInputBase-root': { height: 34 } }}
                />
              </Box>
              {/* Classification is its own page now: the list grows with the page (141 entries is common) and the header sticks to the page scroll. */}
              <Box data-testid="taxonomy-edit-list">
                <Box
                  data-testid="taxonomy-edit-header"
                  sx={{
                    display: { xs: 'none', md: 'grid' },
                    gridTemplateColumns: EDIT_COLUMNS,
                    columnGap: 1,
                    px: 1.75,
                    height: 32,
                    alignItems: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: tokens.surface.sunken,
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: tokens.line.hairline,
                  }}
                >
                  {['Subcategory', 'SKU prefix', 'Shopify Product Category', 'Shopify Product Type'].map((label) => (
                    <Typography key={label} sx={headerSx}>{label}</Typography>
                  ))}
                </Box>
                {editable.length === 0 && (
                  <Typography sx={{ px: 1.75, py: 1.5, fontSize: 13, color: tokens.ink.muted }}>No subcategory matches “{filter.trim()}”.</Typography>
                )}
                {editable.map((entry) => {
                  const value = effective(entry);
                  return (
                    <Box
                      key={entry.id}
                      sx={{ display: 'grid', gridTemplateColumns: EDIT_COLUMNS, columnGap: 1, rowGap: 0.75, alignItems: 'center', px: 1.75, py: 0.75, borderTop: '1px solid', borderColor: tokens.line.hairline }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }} noWrap title={entry.subcategory}>{entry.subcategory}</Typography>
                        <Typography sx={{ fontSize: 12, color: tokens.ink.muted }} noWrap>
                          {entry.department} › {entry.category}{entry.subcategoryCode ? ` · ${entry.subcategoryCode}` : ''}
                        </Typography>
                      </Box>
                      {indexRows ? (
                        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', '& input': v2Mono }}>
                          <CreatableSelect
                            label=""
                            aria-label={`SKU prefix for ${entry.subcategory}`}
                            value={value.prefix}
                            options={prefixOptions}
                            onChange={(next) => edit(entry.id, { skuPrefix: next })}
                            sx={{ width: '100%' }}
                          />
                          {value.prefix.trim() && (
                            countPrefixMatches(indexRows, value.prefix) > 0
                              ? <StatusChip label={`matches ${countPrefixMatches(indexRows, value.prefix)}`} tone="success" />
                              : <StatusChip label="matches nothing" tone="warning" />
                          )}
                        </Box>
                      ) : (
                        <TextField
                          size="small"
                          value={value.prefix}
                          onChange={(event) => edit(entry.id, { skuPrefix: event.target.value })}
                          inputProps={{ 'aria-label': `SKU prefix for ${entry.subcategory}`, style: v2Mono }}
                        />
                      )}
                      <ShopifyCategoryPicker
                        aria-label={`Shopify Product Category for ${entry.subcategory}`}
                        value={value.gid}
                        options={taxonomyOptions}
                        emptyLabel="Not mapped"
                        onChange={(gid) => edit(entry.id, { shopifyCategoryGid: gid })}
                      />
                      <CreatableSelect
                        label=""
                        aria-label={`Shopify Product Type for ${entry.subcategory}`}
                        value={value.type}
                        options={typeOptions}
                        onChange={(next) => edit(entry.id, { productType: next })}
                        placeholder="Shopify Product Type"
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {leaves.length > 0 && (
            <Box sx={{ border: '1px solid', borderColor: tokens.line.default, borderRadius: '10px', overflow: 'hidden' }}>
              <UnmatchedProductsSection
                key={registry.updatedAt ?? ''}
                registry={registry}
                leaves={leaves}
                integrationId={integrationId}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TaxonomyStep;
